import BusinessOutlined from '@mui/icons-material/BusinessOutlined'
import {
  Badge,
  Button,
  CircularProgress,
  DialogActions,
  Paper,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { LocalizedWorkingStepLabel } from 'components/Localization/LocalizedWorkingStepLabel'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { CustomPopper } from 'components/Popper/CustomPopper'
import { WorkingStepsController } from 'controllers/WorkingStepsController'
import { AppFeature } from 'features/AppModules/AppFeature'
import { FeatureKeys } from 'features/AppModules/FeatureKeys'
import { WorkingStepOutsourceDetails } from 'features/BillOfMaterials/WorkingSteps/WorkingStepOutsourceDetails'
import { getOutsourceStatusColor } from 'features/BillOfMaterials/WorkingSteps/workingStepOutsourceStatus'
import { useBomItemDetailsDialog } from 'features/BomItemDetailsDialog/useBomItemDetailsDialog'
import { ConfirmationDialogProps } from 'features/ConfirmationDialog/ConfirmationDialog'
import { useDialogManager } from 'features/DialogManager/useDialogManager'
import { isEqual } from 'lodash'
import { AssemblyHeaderRow, BomItemType } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import React, { SVGAttributes, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BoMItemActivityDto,
  IssueCode,
  WorkingStepDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import {
  RootAppState,
  useAppDispatch,
  useAppSelector,
} from 'store/configureStore'
import { makeStyles } from 'tss-react/mui'
import {
  applyWorkingStepToAllParentFilteredRows,
  removeWorkingStepFromAllParentFilteredRows,
} from '../store/asyncActions/applyWorkingStepToAllParentFilteredRows'
import { saveBomItemIsPurchasingPart } from '../store/asyncActions/saveBomItemIsPurchasingPart'
import { newProjectActions } from '../store/projectReducer'
import { bomItemSelector } from '../store/selectors/bomItemSelector'
import { projectInBuyerViewSelector } from '../store/selectors/projectInBuyerViewSelector'
import { projectOperationIsPendingSelector } from '../store/selectors/projectOperationPendingSelector'
import { projectSelectors } from '../store/selectors/projectSelectors'
import { ActivityRemoveButton } from './ActivityRemoveButton'
import { BomItemActivityComment } from './BomItemActivityComment'
import { SolveWorkingStepNotAvailableButton } from './SolveNotAvailableWorkingStepButton'
import { WorkingStepResourceSelector } from './WorkingStepResourceSelector'
import { useNotAllowedWorkingStep } from './useNotAllowedWorkingStep'

const useStyles = makeStyles({ name: 'ActivityResourceTooltip' })(
  (theme: Theme) => ({
    tooltipRoot: {
      padding: theme.spacing(2, 2, 0),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      minWidth: theme.breakpoints.values.sm,
      border: `2px solid ${theme.palette.divider}`,
    },
    tooltipHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
    },
    workingStepNotAvailableError: {
      display: 'block',
      textAlign: 'left',
      width: '100%',
    },
    workingStepNotAllowedButton: {
      '&:after': {
        content: '" "',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '2px',
        backgroundColor: theme.palette.error.main,
      },
    },
    tooltipBuyerView: {
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(1),
      color: theme.palette.text.primary,
      width: '100%',
      minWidth: theme.breakpoints.values.sm,
      maxWidth: 'unset',
      border: `2px solid ${theme.palette.divider}`,
    },
    resourceTypography: {
      width: '100%',
      textAlign: 'left',
    },
    outsourceIndicatorBadge: {
      '& .MuiBadge-badge': {
        minWidth: theme.spacing(2),
        height: theme.spacing(2),
        padding: 0,
        border: `1px solid ${theme.palette.background.paper}`,
      },
    },
  }),
)

/**
 * The apply to others are visible if the parent is an assemblyHeader and there are more than one partType in the filtered list
 * @param bomItemPointer
 * @returns
 */
const shallShowApplyToOthersSelector =
  (bomItemPointer: BomItemPointer, workingStep: WorkingStepDto) =>
  (state: RootAppState) => {
    if (workingStep.usesRawMaterial) {
      return false
    }

    const parent = bomItemSelector(state, bomItemPointer)?.parentBomItemPointer

    if (parent?.type === BomItemType.assemblyType) {
      const assemblyHeader = bomItemSelector(state, parent) as AssemblyHeaderRow
      return assemblyHeader.filteredPartTypePointers?.length > 1
    }

    return false
  }

type Props = {
  bomItemActivity: BoMItemActivityDto
  bomItemPointer: BomItemPointer
  attributes?: SVGAttributes<SVGElement>
  dataTestId?: string
  forceClose?: boolean
}

const ActivityResourceTooltipComponent = (props: Props) => {
  const getLocalizedWorkingStep = useLocalizedWorkingStepLabel()
  const { classes } = useStyles()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { openDialog } = useDialogManager()
  // const openDialogByURL = useURLDialogManager()
  const openDetailsDialog = useBomItemDetailsDialog()

  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false)

  React.useEffect(() => {
    if (props.forceClose) {
      setIsTooltipOpen(false)
    }
  }, [props.forceClose])

  const operationPending = useAppSelector(
    projectOperationIsPendingSelector(
      applyWorkingStepToAllParentFilteredRows.typePrefix.concat(
        '/',
        props.bomItemPointer.id,
      ),
    ),
    isEqual,
  )

  const { workingStepNotAllowed } = useNotAllowedWorkingStep(
    props.bomItemPointer,
    props.bomItemActivity.primaryWorkingStep,
  )

  const outsourceDetails = props.bomItemActivity.outsourceDetails
  const isOutsourced = Boolean(outsourceDetails)

  const workingStepIconButton = (
    <Badge
      badgeContent={<BusinessOutlined sx={{ fontSize: 12 }} />}
      color={getOutsourceStatusColor(outsourceDetails?.status)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      overlap="circular"
      invisible={!isOutsourced}
      className={classes.outsourceIndicatorBadge}
    >
      <WorkingStepIconSvg
        disableTitle
        workingStepType={props.bomItemActivity.primaryWorkingStep}
        attributes={{
          ...(props.attributes || {
            width: '26px',
          }),
        }}
      />
    </Badge>
  )

  const shallShowApplyToOthers = useAppSelector(
    shallShowApplyToOthersSelector(props.bomItemPointer, props.bomItemActivity),
  )
  const localizedWorkingStep = getLocalizedWorkingStep(
    props.bomItemActivity.primaryWorkingStep,
  )
  const activityLabel = props.bomItemActivity.resource?.name
    ? `${localizedWorkingStep} - ${props.bomItemActivity.resource.name}`
    : localizedWorkingStep

  const removeActivityLabel = t('project:remove-activity', 'remove activity')
  const removeActivityLabelSecondary = t(
    'project:remove-activity-secondary',
    'remove activity from this row',
  )

  const removeActivityFromFilteredRowsLabel = t(
    'project:remove-activity-from-all-filtered-rows',
    'remove activity from all filtered rows',
  )
  const removeActivityFromFilteredRowsHelperText = t(
    'project:remove-activity-from-filtered-rows-helper',
    '{{activity}} will be deleted from the filtered rows',
    {
      replace: {
        activity: activityLabel,
      },
    },
  )
  const removeWorkingStepFromFilteredRowsLabel = t(
    'project:remove-working-step-from-all-filtered-rows',
    'remove working step from all filtered rows',
  )
  const removeWorkingStepFromFilteredRowsHelperText = t(
    'project:remove-working-step-from-filtered-rows-helper',
    '{{workingStep}} will be deleted from the filtered rows, regardless of resource',
    {
      replace: {
        workingStep: localizedWorkingStep,
      },
    },
  )

  const handleApplyToAll = () => {
    dispatch(
      applyWorkingStepToAllParentFilteredRows({
        bomItemPointer: props.bomItemPointer,
        activityId: props.bomItemActivity.id,
      }),
    )
  }

  const isBuyerView = useAppSelector(projectInBuyerViewSelector)
  const projectIsEditable = useAppSelector(
    projectSelectors.ProjectEditableStateSelector,
  )

  const handleRemoveActivity = () => {
    if (projectIsEditable) {
      openDialog('ConfirmationDialog', {
        title: t(
          'project:remove-activity-title',
          'remove activity {{activity}}',
          {
            replace: {
              activity: activityLabel,
            },
          },
        ),
        onCancel: () => {
          setIsTooltipOpen(true)
        },
        onConfirm: async () => {
          try {
            if (
              props.bomItemActivity.primaryWorkingStep ===
              WorkingStepType.Purchasing
            ) {
              dispatch(
                saveBomItemIsPurchasingPart({
                  bomItemPointer: props.bomItemPointer,
                }),
              )
            } else {
              const controller = new WorkingStepsController()
              await controller.handleSaveAndRemoveWorkingSteps(
                [props.bomItemPointer],
                [],
                [
                  {
                    activityId: props.bomItemActivity.id,
                    primaryWorkingStep:
                      props.bomItemActivity.primaryWorkingStep,
                  },
                ],
              )
            }
          } catch (error) {
            ShowException('unable to remove workingstep', error)
          }
        },
      } as ConfirmationDialogProps)
      setIsTooltipOpen(false)
    }
  }

  const handleRemoveActivityFromOtherRows = () => {
    if (projectIsEditable) {
      openDialog('ConfirmationDialog', {
        title: t(
          'project:remove-activity-from-filtered-rows-title',
          'remove activity {{activity}} from all filtered rows',
          {
            replace: {
              activity: activityLabel,
            },
          },
        ),
        onCancel: () => {
          setIsTooltipOpen(true)
        },
        onConfirm: async () => {
          try {
            if (
              props.bomItemActivity.primaryWorkingStep ===
              WorkingStepType.Purchasing
            ) {
              dispatch(
                saveBomItemIsPurchasingPart({
                  bomItemPointer: props.bomItemPointer,
                }),
              )
            } else {
              dispatch(
                removeWorkingStepFromAllParentFilteredRows({
                  bomItemPointer: props.bomItemPointer,
                  workingStepType: props.bomItemActivity.primaryWorkingStep,
                  activityId: props.bomItemActivity.id,
                  resourceId: props.bomItemActivity.resource?.id,
                }),
              )
            }
          } catch (error) {
            ShowException('unable to remove workingstep', error)
          }
        },
      })
      setIsTooltipOpen(false)
    }
  }

  const handleRemoveWorkingStepFromOtherRows = () => {
    if (projectIsEditable) {
      openDialog('ConfirmationDialog', {
        title: t(
          'project:remove-working-step-from-filtered-rows-title',
          'remove working step {{workingStep}} from all filtered rows',
          {
            replace: {
              workingStep: localizedWorkingStep,
            },
          },
        ),
        onCancel: () => {
          setIsTooltipOpen(true)
        },
        onConfirm: async () => {
          try {
            if (
              props.bomItemActivity.primaryWorkingStep ===
              WorkingStepType.Purchasing
            ) {
              dispatch(
                saveBomItemIsPurchasingPart({
                  bomItemPointer: props.bomItemPointer,
                }),
              )
            } else {
              dispatch(
                removeWorkingStepFromAllParentFilteredRows({
                  bomItemPointer: props.bomItemPointer,
                  workingStepType: props.bomItemActivity.primaryWorkingStep,
                  activityId: props.bomItemActivity.id,
                }),
              )
            }
          } catch (error) {
            ShowException('unable to remove workingstep', error)
          }
        },
      })
      setIsTooltipOpen(false)
    }
  }

  const handleOpenWorkingStepDetails = useCallback(() => {
    if (
      props.bomItemActivity.primaryWorkingStep === WorkingStepType.Purchasing &&
      !isBuyerView
    ) {
      openDetailsDialog('articles', {
        bomItemPointer: props.bomItemPointer,
        affectedBomItemPointers: [props.bomItemPointer],
        workingStep: WorkingStepType.Purchasing,
      })

      setIsTooltipOpen(false)

      return
    }

    if (props.bomItemPointer.type === BomItemType.partInstance) {
      return
    }

    openDetailsDialog('workingsteps', {
      bomItemPointer: props.bomItemPointer,
      affectedBomItemPointers: [props.bomItemPointer],
      workingStep: props.bomItemActivity.primaryWorkingStep,
      workingStepActivityId: props.bomItemActivity.id,
    })

    setIsTooltipOpen(false)

    dispatch(newProjectActions.setBomItemInSearch(props.bomItemPointer))
  }, [
    dispatch,
    isBuyerView,
    openDetailsDialog,
    props.bomItemActivity.id,
    props.bomItemActivity.primaryWorkingStep,
    props.bomItemPointer,
  ])

  if (isBuyerView) {
    let tooltipComponent: React.ReactNode = ''

    if (workingStepNotAllowed) {
      tooltipComponent = (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            padding: '0 1em',
          }}
        >
          <LocalizedWorkingStepLabel
            workingStepType={props.bomItemActivity.primaryWorkingStep}
            variant="body1"
          />
          <WorkingStepOutsourceDetails outsourceDetails={outsourceDetails} />
          <Typography
            variant="body2"
            color="error"
            style={{ maxWidth: '300px' }}
          >
            {t(
              'project:portal-not-allowed-working-step-remove',
              'working step not available in the self-service portal. click to remove or contact the supplier',
            )}
          </Typography>
          <div style={{ textAlign: 'right' }}>
            <ActivityRemoveButton
              bomItemType={props.bomItemPointer.type}
              handleRemoveActivity={handleRemoveActivity}
              handleRemoveActivityFromOtherRows={
                handleRemoveActivityFromOtherRows
              }
              handleRemoveWorkingStepFromOtherRows={
                handleRemoveWorkingStepFromOtherRows
              }
              activityType={props.bomItemActivity.primaryWorkingStep}
              removeLabel={removeActivityLabel}
              removeActivityLabelSecondary={removeActivityLabelSecondary}
              removeFromOtherRowsLabel={removeActivityFromFilteredRowsLabel}
              removeFromOtherRowsHelperText={
                removeActivityFromFilteredRowsHelperText
              }
              removeWorkingStepFromOtherRowsLabel={
                removeWorkingStepFromFilteredRowsLabel
              }
              removeWorkingStepFromOtherRowsHelperText={
                removeWorkingStepFromFilteredRowsHelperText
              }
            />
          </div>
        </div>
      )
    } else {
      tooltipComponent = (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            padding: '0 1em',
          }}
        >
          <LocalizedWorkingStepLabel
            workingStepType={props.bomItemActivity.primaryWorkingStep}
            variant="h6"
          />
          <WorkingStepOutsourceDetails outsourceDetails={outsourceDetails} />
          <BomItemActivityComment
            bomItemPointer={props.bomItemPointer}
            bomItemActivityId={props.bomItemActivity.id}
          />
          <div
            style={{
              display: 'flex',
              gap: '1em',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <ActivityRemoveButton
              bomItemType={props.bomItemPointer.type}
              handleRemoveActivity={handleRemoveActivity}
              handleRemoveActivityFromOtherRows={
                handleRemoveActivityFromOtherRows
              }
              handleRemoveWorkingStepFromOtherRows={
                handleRemoveWorkingStepFromOtherRows
              }
              activityType={props.bomItemActivity.primaryWorkingStep}
              removeLabel={removeActivityLabel}
              removeFromOtherRowsLabel={removeActivityFromFilteredRowsLabel}
              removeFromOtherRowsHelperText={
                removeActivityFromFilteredRowsHelperText
              }
              removeWorkingStepFromOtherRowsLabel={
                removeWorkingStepFromFilteredRowsLabel
              }
              removeWorkingStepFromOtherRowsHelperText={
                removeWorkingStepFromFilteredRowsHelperText
              }
            />
            {shallShowApplyToOthers && (
              <Button
                color="primary"
                variant="outlined"
                onClick={handleApplyToAll}
                startIcon={
                  operationPending ? <CircularProgress size={20} /> : null
                }
                size="small"
              >
                {t(
                  'project:apply-ws-to-other-parts',
                  'add working step to other parts',
                )}
              </Button>
            )}
          </div>
        </div>
      )
    }

    return (
      <Tooltip
        title={tooltipComponent}
        classes={{
          tooltip: classes.tooltipBuyerView,
        }}
      >
        <Button
          size="small"
          style={{
            minWidth: 0,
            padding: 0,
            marginRight: '1em',
          }}
          variant={'text'}
          color={workingStepNotAllowed ? 'secondary' : 'inherit'}
          className={
            workingStepNotAllowed ? classes.workingStepNotAllowedButton : ''
          }
          onClick={() =>
            workingStepNotAllowed
              ? handleRemoveActivity()
              : handleOpenWorkingStepDetails()
          }
          data-testid={props.dataTestId}
        >
          {workingStepIconButton}
        </Button>
      </Tooltip>
    )
  }

  return (
    <CustomPopper
      overflowElementId={props.bomItemPointer.id}
      tooltipProps={{
        disableFocusListener: true,
        disableHoverListener: props.forceClose,
        open: props.forceClose ? false : isTooltipOpen,
        onOpen: () => {
          if (!props.forceClose) {
            setIsTooltipOpen(true)
          }
        },
        onClose: () => setIsTooltipOpen(false),
      }}
      AnchorComponent={
        <Badge
          badgeContent={
            props.bomItemActivity.article?.id &&
            !props.bomItemActivity.resource?.id
              ? 1
              : 0
          }
          color="error"
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{
            '& .MuiBadge-badge': {
              top: '2px',
              left: '2px',
            },
          }}
          overlap="circular"
          variant="dot"
          showZero={false}
        >
          <Button
            size="small"
            style={{
              minWidth: 0,
              padding: 0,
              marginLeft: '.5em',
              marginRight: '.5em',
              lineHeight: 'inherit',
            }}
            // style={{ minWidth: 0, padding: 0, marginRight: '1em' }}
            variant={'text'}
            color={workingStepNotAllowed ? 'secondary' : 'inherit'}
            className={
              workingStepNotAllowed ? classes.workingStepNotAllowedButton : ''
            }
            onClick={handleOpenWorkingStepDetails}
            data-testid={props.dataTestId}
          >
            {workingStepIconButton}
          </Button>
        </Badge>
      }
      TooltipComponent={
        <Paper elevation={4} className={classes.tooltipRoot}>
          <div className={classes.tooltipHeader}>
            <Typography variant="h6">
              {getLocalizedWorkingStep(
                props.bomItemActivity.primaryWorkingStep,
              )}
              {workingStepNotAllowed && (
                <Typography
                  variant="caption"
                  color="error"
                  className={classes.workingStepNotAvailableError}
                >
                  {t(`errors:${IssueCode.WorkingStepNotAvailable}`)}
                </Typography>
              )}
            </Typography>
          </div>
          <AppFeature featureKey={FeatureKeys.quotation}>
            {!isOutsourced && (
              <WorkingStepResourceSelector
                workingStep={props.bomItemActivity}
                bomItemActivityId={props.bomItemActivity.id}
                bomItemPointersAndActivities={[
                  {
                    bomItemPointer: props.bomItemPointer,
                    activityId: props.bomItemActivity.id,
                  },
                ]}
                fullWidth
                typographyClassname={classes.resourceTypography}
              />
            )}
          </AppFeature>
          <BomItemActivityComment
            bomItemPointer={props.bomItemPointer}
            bomItemActivityId={props.bomItemActivity.id}
          />
          <DialogActions>
            <SolveWorkingStepNotAvailableButton
              bomItemPointer={props.bomItemPointer}
              workingStepType={props.bomItemActivity.primaryWorkingStep}
            />
            {projectIsEditable &&
              props.bomItemPointer.type !== BomItemType.partInstance && (
                <ActivityRemoveButton
                  bomItemType={props.bomItemPointer.type}
                  handleRemoveActivity={handleRemoveActivity}
                  handleRemoveActivityFromOtherRows={
                    handleRemoveActivityFromOtherRows
                  }
                  handleRemoveWorkingStepFromOtherRows={
                    handleRemoveWorkingStepFromOtherRows
                  }
                  activityType={props.bomItemActivity.primaryWorkingStep}
                  removeLabel={removeActivityLabel}
                  removeFromOtherRowsLabel={removeActivityFromFilteredRowsLabel}
                  removeFromOtherRowsHelperText={
                    removeActivityFromFilteredRowsHelperText
                  }
                  removeWorkingStepFromOtherRowsLabel={
                    removeWorkingStepFromFilteredRowsLabel
                  }
                  removeWorkingStepFromOtherRowsHelperText={
                    removeWorkingStepFromFilteredRowsHelperText
                  }
                />
                // <Button
                //   color="secondary"
                //   variant="text"
                //   onClick={handleRemoveActivity}
                // >
                //   {t(
                //     'project:remove-working-step-tooltip',
                //     'remove working step'
                //   )}
                // </Button>
              )}

            {shallShowApplyToOthers && (
              <Button
                color="primary"
                variant="text"
                onClick={handleApplyToAll}
                startIcon={
                  operationPending ? <CircularProgress size={20} /> : null
                }
              >
                {t(
                  'project:apply-ws-to-other-parts',
                  'add working step to other parts',
                )}
              </Button>
            )}
          </DialogActions>
        </Paper>
      }
    />
  )
}

const ActivityResourceTooltip = React.memo(
  ActivityResourceTooltipComponent,
  isEqual,
)

export default ActivityResourceTooltip
